import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Copy, Pencil, Trash2 } from "lucide-react";
import type { GroupSummary } from "../../types";
import { isGroupLocked } from "../../types";
import { DataGrid, type GridColumn, type GridSort } from "../DataGrid";
import { IconButton } from "../IconButton";
import { IhqTypePill, LockPill, StatusPill } from "../Pills";
import { SalesforceRecordLink } from "../SalesforceRecordLink";
import { formatDate, truncate } from "../../utils/format";

interface GroupsGridProps {
  caption: string;
  groups: readonly GroupSummary[];
  isLoading: boolean;
  emptyState: ReactNode;
  defaultSort: GridSort;
  onEdit: (group: GroupSummary) => void;
  onCopy: (group: GroupSummary) => void;
  onDelete: (group: GroupSummary) => void;
  children?: never;
}

/** Shared results grid for Recent Groups and Search for Group. Status is its own aligned column. */
export function GroupsGrid({ caption, groups, isLoading, emptyState, defaultSort, onEdit, onCopy, onDelete }: GroupsGridProps) {
  const columns = useMemo<GridColumn<GroupSummary>[]>(
    () => [
      {
        key: "name",
        header: "Group Name",
        sortValue: (group) => group.name,
        widthClassName: "whitespace-nowrap",
        render: (group) => (
          <>
            <Link to={`/uw/groups/${group.id}`} className="font-medium text-brand-navy underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded">
              {group.name}
            </Link>
            <span className="mx-1.5 text-gray-300" aria-hidden="true">
              |
            </span>
            <span title={group.supersededByGroupId !== null ? `Version ${group.version}, replaced by a newer version` : `Version ${group.version}`} className={`font-mono text-xs ${group.supersededByGroupId !== null ? "text-gray-400 line-through" : "text-gray-500"}`}>
              v{group.version}
            </span>
          </>
        ),
      },
      { key: "lock", header: "Locked", hideHeader: true, align: "center", sortValue: (group) => (isGroupLocked(group) ? 1 : 0), widthClassName: "w-8 px-1", render: (group) => <LockPill group={group} compact /> },
      { key: "status", header: "Workbench\nStatus", sortValue: (group) => group.status, widthClassName: "whitespace-nowrap", render: (group) => <StatusPill status={group.status} /> },
      { key: "underwriter", header: "Underwriter", sortValue: (group) => group.underwriterName, widthClassName: "whitespace-nowrap", render: (group) => group.underwriterName ?? <span className="text-gray-400">Unassigned</span> },
      { key: "effectiveDate", header: "Effective\nDate", sortValue: (group) => group.effectiveDate, widthClassName: "whitespace-nowrap", render: (group) => formatDate(group.effectiveDate) },
      { key: "ihqType", header: "IHQ Type", sortValue: (group) => group.ihqType, render: (group) => <IhqTypePill ihqType={group.ihqType} /> },
      { key: "notes", header: "Notes", render: (group) => (group.notes ? <span title={group.notes} className="block max-w-40 truncate">{truncate(group.notes, 60)}</span> : <span className="text-gray-400">None</span>) },
      { key: "createdProcess", header: "Created\nProcess", sortValue: (group) => group.createdProcess, render: (group) => group.createdProcess },
      { key: "createdUtc", header: "Created\nDate", sortValue: (group) => group.createdUtc, widthClassName: "whitespace-nowrap", render: (group) => formatDate(group.createdUtc) },
      { key: "lastOpened", header: "Last Opened", sortValue: (group) => group.lastOpenedUtc, hideHeader: true, render: () => null, widthClassName: "w-0 p-0" },
      { key: "opportunityId", header: "Opportunity\nID", render: (group) => <SalesforceRecordLink object="Opportunity" recordId={group.opportunityId} /> },
    ],
    [],
  );

  return (
    <DataGrid
      density="compact"
      caption={caption}
      columns={columns}
      rows={groups}
      rowKey={(group) => group.id}
      isLoading={isLoading}
      emptyState={emptyState}
      defaultSort={defaultSort}
      actionsHeader="Group actions"
      renderActions={(group) => (
        <span className="inline-flex">
          <IconButton icon={Pencil} label={`Edit ${group.name}`} size="sm" onClick={() => onEdit(group)} />
          <IconButton icon={Copy} label={`Copy ${group.name}`} size="sm" onClick={() => onCopy(group)} />
          <IconButton icon={Trash2} label={`Delete ${group.name}`} tone="danger" size="sm" onClick={() => onDelete(group)} />
        </span>
      )}
    />
  );
}
